import { useCallback, useEffect, useState } from 'react';

import { getHome } from '../../../shared/api/serieRequest';

import SerieSheet from '../../../series/components/SerieSheet';
import { Category } from '../../../shared/types/Category';
import { Serie } from '../../../shared/types/Serie';
import HomeVisual, { isCollectionCategory } from '../../components/v2/HomeVisual';
import UnlockSuccessDialog from '../../components/v2/UnlockSuccessDialog';

import { useLocation } from 'react-router-dom';
import { UnlockedSerie, unlockSerieProf } from '../../../shared/api/request';
import HeaderV2 from '../../../shared/components/HeaderV2';
import { useAuth } from '../../../shared/context/TokenContext';
import '../../../shared/css/basicStyles.css';
import '../../../shared/css/fontStyle.css';

const Home = () => {
  const { isEdifice, isTeacher } = useAuth();
  const { state } = useLocation();

  const [assignations, setAssignations] = useState<Serie[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [playlist, setPlaylist] = useState<Serie[]>([]);

  const [selectedSerie, setSelectedSerie] = useState<Serie | null>(null);

  const [isLoading, setIsLoading] = useState(true);
  const [errorLoading, setErrorLoading] = useState<string | null>(null);

  const [unlockedSeries, setUnlockedSeries] = useState<UnlockedSerie[] | null>(null);
  const [highlightedSerieIds, setHighlightedSerieIds] = useState<number[]>([]);

  const fetchHome = useCallback(async () => {
    const type = isTeacher && isTeacher() ? 'prof' : 'user';
    const res = await getHome(type);
    // console.log('🚀 ~ loadLib ~ res:', res);
    if (res) {
      setAssignations(res.assignations ?? []);
      setCategories(res.cats ?? []);
      setPlaylist(res.playlist ?? []);
    }
    return res;
  }, [isTeacher]);

  const loadLib = useCallback(async () => {
    setIsLoading(true);
    setErrorLoading(null);
    const res = await fetchHome();
    if (res) {
      // If a serie is passed in state, open the panel directly
      if (state && state.serieId && res.cats) {
        const category = res.cats.find(((cat: Category) =>
          !isCollectionCategory(cat) && cat.content.find((serie: Serie) => serie.id === state.serieId)
        ));

        if(category) {
          const selected = category?.content.find((serie: Serie) => serie.id === state.serieId);
        
          if (selected) setSelectedSerie(selected);
        }
      }

    } else setErrorLoading('Erreur de chargement des séries');
    setIsLoading(false);
  }, [fetchHome, state]);

  const onClickSerie = (serie: Serie) => setSelectedSerie(serie);

  const onUnlockSerie = async (code: string) => {
    const res = await unlockSerieProf(code);
    if (res.isOk) {
      // Refresh the lists in place so the unlocked series show up without reloading the page
      await fetchHome();
      setHighlightedSerieIds(res.series.map(s => s.idSerie));
      setUnlockedSeries(res.series);
    }
    return res;
  };

  const findSerie = (id: number) =>
    [...playlist, ...assignations, ...categories.filter(c => !isCollectionCategory(c)).flatMap(c => c.content)].find(
      s => s.id === id,
    );

  const onOpenUnlockedSerie = (unlocked: UnlockedSerie) => {
    setUnlockedSeries(null);
    const serie = findSerie(unlocked.idSerie);
    if (serie) {
      onClickSerie(serie);
      return;
    }

    // Not listed in the home: rebuild what the panel needs from the unlock response
    const [, uid, img] = unlocked.img.match(/series\/([^/]+)\/couvertures\/(.+)$/) ?? [];
    onClickSerie({ id: unlocked.idSerie, name: unlocked.name, uid, img } as Serie);
  };

  const onCloseUnlockDialog = () => {
    setUnlockedSeries(null);
    // Bring the freshly unlocked series into view once the dialog is gone
    setTimeout(() => {
      document.querySelector('.nbk-cover--highlighted')?.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'nearest' });
    }, 300);
  };

  useEffect(() => {
    loadLib();
  }, [loadLib]);

  return (
    <div className="mainContainer">
      <HeaderV2
        selected="home"
        type={isEdifice && isEdifice() ? (isTeacher && isTeacher() ? 'teacher' : 'edifice-user') : 'user'}
      />
      <HomeVisual
        assignations={assignations}
        cats={categories}
        isLoading={isLoading}
        errorLoading={errorLoading}
        playlist={playlist}
        highlightedSerieIds={highlightedSerieIds}
        onClickSerie={onClickSerie}
        onRetry={loadLib}
        onUnlockSerie={onUnlockSerie}
      />
      <UnlockSuccessDialog series={unlockedSeries} onClose={onCloseUnlockDialog} onOpenSerie={onOpenUnlockedSerie} />
      <SerieSheet serie={selectedSerie} onClose={() => setSelectedSerie(null)} />
    </div>
  );
};

export default Home;
